import { OptionMap } from './optionTypes';
import { CollaboratorDto } from '@/api/ApiClient';

/**
 * User types that can be searched for name resolution
 */
export type UserScope = 'researcher' | 'faculty' | 'external' | 'in-house' | 'all';

/** Finds a collaborator by id across the given user scope. */
export const findCollaboratorById = (
  userId: string,
  options: Partial<OptionMap>,
  scope: UserScope = 'all'
): CollaboratorDto | undefined => {
  if (!userId) return undefined;

  switch (scope) {
    case 'external':
      return options.users?.find((user) => user.id === userId);
    case 'in-house':
      return options.staff?.find((user) => user.id === userId);
    case 'all':
    default:
      return options.staff?.find((user) => user.id === userId) ?? options.users?.find((user) => user.id === userId);
  }
};

type CollaboratorNameFields = {
  id?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  contactEmail?: string;
  email?: string;
};

/** Display name: fullName, else "First Last", else id. */
export const collaboratorDisplayName = (user: CollaboratorNameFields): string =>
  user.fullName?.trim() || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.id || '';

/** Formats a collaborator as "First Last (Institution)" for pickers and grids. */
export const formatCollaboratorLabel = (user: CollaboratorDto): string => {
  const name = collaboratorDisplayName(user);
  const institution = user.institution?.trim();
  return institution ? `${name} (${institution})` : name;
};

/** Formats a collaborator as "First Last (email)" for same-name picker disambiguation. */
export const formatCollaboratorOptionLabel = (user: CollaboratorNameFields): string => {
  const name = collaboratorDisplayName(user);
  const email = user.contactEmail?.trim() || user.email?.trim();
  return email ? `${name} (${email})` : name;
};

/**
 * Parses typed picker text into create-dialog fields.
 * Last token with `@` is treated as email; remaining words are first / last name.
 */
export const parseCollaboratorCreateInput = (
  input: string
): { firstName?: string; lastName?: string; email?: string } => {
  const tokens = input.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return {};

  let email: string | undefined;
  if (tokens[tokens.length - 1]?.includes('@')) {
    email = tokens.pop();
  }
  if (tokens.length === 0) return { email };
  if (tokens.length === 1) return { firstName: tokens[0], email };
  return { firstName: tokens[0], lastName: tokens.slice(1).join(' '), email };
};

/**
 * Resolves a user ID to a display name by searching through the specified user scopes
 * @param userId - The user ID to resolve
 * @param options - The options map containing user data
 * @param scope - Which user types to search in (default: 'all')
 * @returns The display name or the original userId if not found
 */
export const resolveUserName = (userId: string, options: Partial<OptionMap>, scope: UserScope = 'all'): string => {
  if (!userId) return 'N/A';
  if (userId.startsWith('_') || userId === '-') return 'System';

  const found = findCollaboratorById(userId, options, scope);
  if (found) return `${found.firstName} ${found.lastName}`;

  return userId;
};

/**
 * Resolves a user ID to a contact email (backend-provided `contactEmail`), or undefined when unknown.
 * Callers should render plain text rather than guess an address when this is undefined.
 */
export const resolveUserEmail = (
  userId: string,
  options: Partial<OptionMap>,
  scope: UserScope = 'all'
): string | undefined => {
  if (!userId) return undefined;
  return findCollaboratorById(userId, options, scope)?.contactEmail?.trim() || undefined;
};

/**
 * Resolves a user ID to "Name (Institution)" when institution is available.
 */
export const resolveUserWithInstitution = (
  userId: string,
  options: Partial<OptionMap>,
  scope: UserScope = 'all'
): string => {
  if (!userId) return '';

  const found = findCollaboratorById(userId, options, scope);
  if (found) return formatCollaboratorLabel(found);
  if (userId.startsWith('_')) return 'System';

  return userId;
};

/**
 * Resolves multiple user IDs to display names
 * @param userIds - Array of user IDs to resolve
 * @param options - The options map containing user data
 * @param scope - Which user types to search in (default: 'all')
 * @param separator - String to join multiple names (default: ', ')
 * @returns The joined display names or 'N/A' if no users
 */
export const resolveUserNames = (
  userIds: string[] | undefined,
  options: Partial<OptionMap>,
  scope: UserScope = 'all',
  separator: string = ', '
): string => {
  if (!userIds || userIds.length === 0) return 'N/A';

  const names = userIds.map((id) => resolveUserName(id, options, scope));
  return names.join(separator);
};
